import React, { useCallback, useRef, useState } from 'react';
import { Keys } from '../../utils/keysUtil';

export function useCheckboxTreeKeyboardSupport(dataTest: string) {
  const [focusedNode, setFocusedNode] = useState<string | undefined>();
  const [lastFocusedNode, setLastFocusedNode] = useState<string | undefined>();
  const listWrapperRef = useRef<HTMLDivElement>(null);
  const getAllRenderedNodes = useCallback(() => getAllNodes(listWrapperRef.current), []);

  // Retrieve and store value of a node that going to be focused
  const focusNode = (event?: { target: EventTarget }) => {
    const id = getClosestNode(event)?.id ?? undefined;
    if (id) {
      setFocusedNode(id);
    }
    // move focus back to wrapper to keep control over keyboard events - component used for virtualization, implements arrows for scrolling purposes
    listWrapperRef.current?.focus();
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (Keys.oneOf(e, 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ')) {
        e.preventDefault();
        e.stopPropagation();
        const allRenderedNodes = getAllRenderedNodes();
        const currentNodeIndex = allRenderedNodes.findIndex((it) => it.getAttribute('id') === focusedNode);
        // To be improved in the future - now if current node cannot be found in the rendered list -
        // because e.g. was long scrolled down then pick middle one from the current list
        if (currentNodeIndex === -1) {
          setFocusedNode(allRenderedNodes[Math.floor(allRenderedNodes.length / 2)]?.id);
          return;
        }
        // Focus prev item
        if (Keys.oneOf(e, 'ArrowUp')) {
          const prevNode = currentNodeIndex > 0 ? allRenderedNodes[currentNodeIndex - 1] : undefined;
          if (prevNode) {
            setFocusedNode(prevNode.id ?? undefined);
            scrollIntoView(prevNode);
          }
        }
        // Focus next item
        if (Keys.oneOf(e, 'ArrowDown')) {
          const nextNode =
            currentNodeIndex < allRenderedNodes.length - 1 ? allRenderedNodes[currentNodeIndex + 1] : undefined;
          if (nextNode) {
            setFocusedNode(nextNode.id ?? undefined);
            scrollIntoView(nextNode);
          }
        }
        // Expand current tree if applicable
        if (Keys.oneOf(e, 'ArrowRight')) {
          (
            allRenderedNodes[currentNodeIndex]?.querySelector('[data-test="expand-icon"] button') as HTMLButtonElement
          )?.click();
        }
        // Collapse current tree if applicable
        if (Keys.oneOf(e, 'ArrowLeft')) {
          (
            allRenderedNodes[currentNodeIndex]?.querySelector('[data-test="collapse-icon"] button') as HTMLButtonElement
          )?.click();
        }
        // Toggle checkbox in current node
        if (Keys.oneOf(e, ' ', 'Enter')) {
          (allRenderedNodes[currentNodeIndex]?.querySelector('input[type="checkbox"]') as HTMLInputElement)?.click();
        }
      }
    },
    [focusedNode, getAllRenderedNodes]
  );

  // We temporary keep information that element was clicked to ignore next focus event.
  const handleClick = useCallback(() => {
    listWrapperRef.current?.setAttribute(CLICKED_DOM_ATTR, 'true');
  }, []);

  const handleFocus = useCallback(() => {
    // Check if handleClick was executed. If yes then pass it through - node (row) click handlers will mark target element
    const willReceiveClick = listWrapperRef.current?.getAttribute(CLICKED_DOM_ATTR);
    if (!focusedNode && !willReceiveClick) {
      const allNodes = getAllRenderedNodes();
      const lastFocusedNodeIndex = lastFocusedNode ? allNodes.findIndex((it) => it.id === lastFocusedNode) : -1;
      const node = lastFocusedNodeIndex !== -1 ? allNodes[lastFocusedNodeIndex] : allNodes[0];

      setFocusedNode(node?.id);
      scrollIntoView(node);
    }
  }, [focusedNode, getAllRenderedNodes, lastFocusedNode]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (!isPartOfComponent(dataTest, e.relatedTarget as HTMLElement)) {
        setLastFocusedNode(focusedNode);
        setFocusedNode(undefined);
        listWrapperRef.current?.removeAttribute(CLICKED_DOM_ATTR);
      }
    },
    [dataTest, focusedNode]
  );

  return {
    handleBlur,
    handleFocus,
    handleClick,
    handleKeyDown,
    focusNode,
    focusedNode,
    listWrapperRef
  };
}

function getAllNodes(parentElement?: HTMLElement | null): HTMLDivElement[] {
  return Array.from(parentElement?.querySelectorAll('.node') ?? []);
}

function getClosestNode(event?: { target: EventTarget }): HTMLDivElement | undefined {
  return ((event?.target as Element)?.closest?.('.node') as HTMLDivElement) ?? undefined;
}

function isPartOfComponent(dataTest: string, element?: Element | null): boolean {
  return !!(element?.closest?.(`[data-test="${dataTest}"]`) as HTMLDivElement);
}

function scrollIntoView(element: Element | undefined) {
  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

const CLICKED_DOM_ATTR = 'data-clicked';
